import type { ReactNode } from 'react'

/** Severity drives the colour palette — green/yellow/red/neutral. */
export type MetricSeverity = 'ok' | 'warn' | 'critical' | 'neutral'

interface MetricBadgeProps {
  /** Display text. Pass `'—'` (DASH) when no data is available. */
  label: ReactNode
  /** Optional severity colour; defaults to neutral grey. */
  severity?: MetricSeverity
  /** Native title for hover/long-press tooltip. */
  title?: string
}

const PALETTE: Record<MetricSeverity, string> = {
  ok: 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300',
  warn: 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300',
  critical: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300',
  neutral: 'bg-gray-100 dark:bg-gray-800/40 text-gray-600 dark:text-gray-300',
}

/**
 * Compact pill badge for one telemetry metric (battery, SIM, signal,
 * carrier). Used inside `PhoneRow` (desktop table cell) and
 * `PhoneMobileCard` (mobile metrics row).
 */
export function MetricBadge({ label, severity = 'neutral', title }: MetricBadgeProps) {
  return (
    <span
      className={`inline-flex items-center text-[11px] font-medium px-1.5 py-0.5 rounded ${PALETTE[severity]}`}
      title={title}
    >
      {label}
    </span>
  )
}
